import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "antd";
import { CheckOutlined, CopyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

/**
 * Small copy-to-clipboard icon for identifiers (VIN, reservation no.). Inside a table row it only
 * appears while the row is hovered (see `.sc-copy` in data-table.css), so it doesn't add clutter.
 */
function CopyButton({ value }) {
    const { t } = useTranslation("table");
    const [copied, setCopied] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    if (!value) return null;

    const handleCopy = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        try {
            await navigator.clipboard.writeText(String(value));
            setCopied(true);
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard can be unavailable (insecure context); nothing useful to do.
        }
    };

    return (
        <Tooltip title={copied ? t("copied") : t("copy")}>
            <button
                type="button"
                onClick={handleCopy}
                aria-label={t("copy")}
                className="sc-copy inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-[#5D5DF6]">
                {copied ? <CheckOutlined className="text-[11px] text-green-600" /> : <CopyOutlined className="text-[11px]" />}
            </button>
        </Tooltip>
    );
}

CopyButton.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CopyButton;
